import axios from 'axios';
import { Site, Customer, ScanHistoryApiItem, ScanStartResponse, ScanObject, ScanResultsApiResponse, RawNetworkDevice, SNMPQueryStatus, SSHQueryStatus } from '../types/network';
import { ServerConnectionError, redirectToServerError } from '../utils/errors';

// Centralized error handling utility
const handleApiError = (error: unknown, defaultMessage: string): Error => {
  if (axios.isAxiosError(error)) {
    // Check for network/connection errors
    if (error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_TIMED_OUT' ||
        error.code === 'ERR_NAME_NOT_RESOLVED' ||
        error.code === 'ECONNREFUSED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !error.response) {
      
      // Redirect to server error page immediately
      setTimeout(() => redirectToServerError(), 100);
      
      // Return custom error for any remaining error handling
      return new ServerConnectionError();
    }
    
    // Handle HTTP response errors
    if (error.response) {
      const status = error.response.status;
      const statusText = error.response.statusText;
      return new Error(`${defaultMessage}: ${status} ${statusText}`);
    }
    
    // Handle other axios errors
    return new Error(`${defaultMessage}: ${error.message}`);
  }
  
  // Handle non-axios errors
  return new Error(defaultMessage);
};

// Network device types
export interface NetworkDevice {
  device_ip: string;
  protocol: string;
  local_port_number: number;
  local_port_name: string;
  rem_chassis_id: string;
  rem_chassis_vendor: string;
  management_ip: string;
  rem_sys_name: string;
  rem_port_id: string;
  device_type_name: string;
  sys_description: string;
}

// Enhanced network device with merged information
export interface MergedNetworkDevice {
  device_ip: string;
  protocols: string[]; // All protocols that discovered this device
  local_port_numbers: number[]; // All port connections
  rem_chassis_id: string;
  rem_chassis_vendor: string;
  management_ip: string;
  rem_sys_name: string;
  rem_port_id: string;
  device_type_name: string;
  discovery_methods: string; // Combined protocol string for display
  connection_count: number; // Number of port connections
}

export interface ListDevicesResponse {
  network_devices: NetworkDevice[];
}

// API configuration
const NETWORK_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5555';
const API_TOKEN = process.env.REACT_APP_API_TOKEN;

// Create axios instance
const apiClient = axios.create({
  baseURL: NETWORK_API_BASE_URL,
  headers: {
    'Authorization': `Bearer ${API_TOKEN}`,
    'Accept': 'application/json',
  },
});

// Network API functions
export const fetchListDevices = async (siteId: string): Promise<ListDevicesResponse> => {
  if (!API_TOKEN) {
    throw new Error('API token not configured');
  }
  try {
    const response = await apiClient.get<ListDevicesResponse>(`/list-devices?site_id=${siteId}`);
    return response.data;
  } catch (error) {
    console.error('fetchListDevices failed for siteId:', siteId);
    console.error('Full error object:', error);
    
    if (axios.isAxiosError(error)) {
      console.error('Axios error details:');
      console.error('- Status:', error.response?.status);
      console.error('- Status Text:', error.response?.statusText);
      console.error('- Response Data:', error.response?.data);
      console.error('- Request URL:', error.config?.url);
      
      throw new Error(`Failed to fetch devices: ${error.response?.status} ${error.response?.statusText}`);
    }
    
    console.error('Non-axios error:', error);
    throw new Error('Failed to fetch devices');
  }
};

// Scan start result interface
export interface ScanStartResult {
  scanStarted: boolean;
  scanOngoing: boolean;
  scanId?: string;
  scanObject?: ScanObject;
  error?: string;
}

// Seed file scan result interface
export interface SeedFileScanResult {
  scanId?: string;
  scanObject?: ScanObject;
  scanStarted: boolean;
  scanOngoing: boolean;
  error?: string;
}

// New scan API functions
export const startScan = async (siteId: string, useSeedApi?: boolean): Promise<ScanStartResult> => {
  if (!API_TOKEN) {
    throw new Error('API token not configured');
  }

  try {
    // Build URL with optional use_seed_api query parameter
    let url = `/sites/${encodeURIComponent(siteId)}/scans`;
    if (useSeedApi === true) {
      url += '?use_seed_api=true';
    }
    
    const response = await apiClient.post<ScanStartResponse>(url);
    return {
      scanStarted: true,
      scanOngoing: false,
      scanId: response.data.Scan.ID,
      scanObject: response.data.Scan
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      // Handle 406 "Scan ongoing" specifically
      if (error.response?.status === 406) {
        const errorData = error.response.data;
        if (errorData?.error === 'Scan ongoing.') {
          return {
            scanStarted: false,
            scanOngoing: true
          };
        }
      }
      
      console.log(error);
      throw new Error(`Failed to start scan: ${error.response?.status} ${error.response?.statusText}`);
    }
    throw new Error('Failed to start scan');
  }
};

// WebSocket URL builder for scan-specific tracking
export const buildScanWebSocketUrl = (scanId: string): string => {
  if (!API_TOKEN) {
    throw new Error('API token not configured');
  }

  // Always use the backend URL for WebSocket connections (proxy doesn't handle WebSocket)
  const backendUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5555';
  const wsBaseUrl = backendUrl
    .replace('http://', 'ws://')
    .replace('https://', 'wss://');
  
  const wsUrl = `${wsBaseUrl}/scans/${encodeURIComponent(scanId)}/updates?token=${API_TOKEN}`;
  
  console.log('Scan WebSocket URL:', wsUrl);
  return wsUrl;
};

// Get API token for WebSocket authentication
export const getApiToken = (): string => {
  if (!API_TOKEN) {
    throw new Error('API token not configured');
  }
  return API_TOKEN;
};

// Utility ping scan function
export interface PingResponseItem {
  IPAddress: string;
  RoundTripTimeMilliseconds: number;
}

export type UtilPingResponse = PingResponseItem[];

export const utilPing = async (ipRange: string): Promise<UtilPingResponse> => {
  if (!API_TOKEN) {
    throw new Error('API token not configured');
  }

  try {
    const response = await apiClient.get<UtilPingResponse>(`/util/ping?ip_range=${encodeURIComponent(ipRange)}`);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(`Failed to ping IP range: ${error.response?.status} ${error.response?.statusText}`);
    }
    throw new Error('Failed to ping IP range');
  }
};

// Customer management API functions
export const fetchCustomers = async (): Promise<Customer[]> => {
  if (!API_TOKEN) {
    throw new Error('API token not configured');
  }

  try {
    const response = await apiClient.get<Customer[]>('/customers');
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to fetch customers');
  }
};

export const createCustomer = async (name: string): Promise<Customer> => {
  if (!API_TOKEN) {
    throw new Error('API token not configured');
  }

  if (!name || name.trim().length === 0) {
    throw new Error('Customer name is required');
  }

  try {
    const response = await apiClient.post<Customer>(`/customers?name=${encodeURIComponent(name.trim())}`);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 400) {
        throw new Error('Invalid customer name or customer already exists');
      }
      throw new Error(`Failed to create customer: ${error.response?.status} ${error.response?.statusText}`);
    }
    throw new Error('Failed to create customer');
  }
};

export const fetchCustomerSites = async (customerId: string): Promise<Site[]> => {
  if (!API_TOKEN) {
    throw new Error('API token not configured');
  }

  if (!customerId || customerId.trim().length === 0) {
    throw new Error('Customer ID is required');
  }

  try {
    const response = await apiClient.get<Site[]>(`/customers/${encodeURIComponent(customerId)}/sites`);
    return response.data;
  } catch (error) {
    // Handle 404 specifically for customer not found
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      throw new Error('Customer not found');
    }
    throw handleApiError(error, 'Failed to fetch customer sites');
  }
};

// Site management API functions
export const fetchSites = async (): Promise<Site[]> => {
  if (!API_TOKEN) {
    throw new Error('API token not configured');
  }

  try {
    const response = await apiClient.get<Site[]>('/sites');
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to fetch sites');
  }
};

export const createSite = async (name: string, customerId?: string, vlid?: string): Promise<Site> => {
  if (!API_TOKEN) {
    throw new Error('API token not configured');
  }

  if (!name || name.trim().length === 0) {
    throw new Error('Site name is required');
  }

  try {
    let url = `/sites?name=${encodeURIComponent(name.trim())}`;
    if (customerId) {
      url += `&customer_id=${encodeURIComponent(customerId)}`;
    }
    if (vlid && vlid.trim().length > 0) {
      url += `&vlid=${encodeURIComponent(vlid.trim())}`;
    }
    
    const response = await apiClient.post<Site>(url);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 400) {
        throw new Error('Invalid site name or site already exists');
      }
      throw new Error(`Failed to create site: ${error.response?.status} ${error.response?.statusText}`);
    }
    throw new Error('Failed to create site');
  }
};

// Seed file scan API function
export const startSeedFileScan = async (
  siteId: string, 
  seedFile: File,
  onUploadProgress?: (progress: number) => void,
  useSeedApi?: boolean
): Promise<SeedFileScanResult> => {
  if (!API_TOKEN) {
    throw new Error('API token not configured');
  }

  if (!siteId || siteId.trim().length === 0) {
    throw new Error('Site ID is required');
  }

  if (!seedFile) {
    throw new Error('Seed file is required');
  }

  // Validate file type
  if (!seedFile.name.match(/\.(xlsx|xls)$/i)) {
    throw new Error('Seed file must be an Excel file (.xlsx or .xls)');
  }

  // Validate file size (10MB limit)
  if (seedFile.size > 10 * 1024 * 1024) {
    throw new Error('Seed file must be smaller than 10MB');
  }

  try {
    // Build URL with optional use_seed_api query parameter
    let url = `/sites/${encodeURIComponent(siteId)}/scans`;
    if (useSeedApi === true) {
      url += '?use_seed_api=true';
    }
    
    const response = await apiClient.post<ScanStartResponse>(url, seedFile, {
      headers: {
        'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onUploadProgress(progress);
        }
      }
    });
    console.log('response')
    console.log(response)

    return {
      scanStarted: true,
      scanOngoing: false,
      scanId: response.data.Scan.ID,
      scanObject: response.data.Scan
    };
  } catch (error) {
    console.error(error)
    if (axios.isAxiosError(error)) {
      // Handle 406 "Scan ongoing" specifically
      if (error.response?.status === 406) {
        const errorData = error.response.data;
        if (errorData?.error === 'Scan ongoing.') {
          return {
            scanStarted: false,
            scanOngoing: true
          };
        }
      }
      
      if (error.response?.status === 400) {
        throw new Error('Invalid seed file format or content');
      }
      
      if (error.response?.status === 413) {
        throw new Error('Seed file is too large');
      }
      
      throw new Error(`Failed to start seed file scan: ${error.response?.status} ${error.response?.statusText}`);
    }
    throw new Error('Failed to start seed file scan');
  }
};

// Scan history API functions
export const fetchSiteScans = async (siteId: string): Promise<ScanHistoryApiItem[]> => {
  if (!API_TOKEN) {
    throw new Error('API token not configured');
  }

  if (!siteId || siteId.trim().length === 0) {
    throw new Error('Site ID is required');
  }

  try {
    const response = await apiClient.get<ScanHistoryApiItem[]>(`/sites/${encodeURIComponent(siteId)}/scans`);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        throw new Error('Site not found or no scans available');
      }
      throw new Error(`Failed to fetch scan history: ${error.response?.status} ${error.response?.statusText}`);
    }
    throw new Error('Failed to fetch scan history');
  }
};

// Helper function to map raw API device to NetworkDevice format
const mapRawDeviceToNetworkDevice = (rawDevice: RawNetworkDevice): NetworkDevice => {
  return {
    device_ip: rawDevice.device_ip,
    protocol: rawDevice.protocol,
    local_port_number: rawDevice.local_port_number,
    local_port_name: rawDevice.local_port_name,
    rem_chassis_id: rawDevice.rem_chassis_id,
    rem_chassis_vendor: rawDevice.rem_chassis_vendor,
    management_ip: rawDevice.management_ip,
    rem_sys_name: rawDevice.rem_sys_name,
    rem_port_id: rawDevice.rem_port_id,
    device_type_name: rawDevice.device_type_name,
    sys_description: rawDevice.sys_description
  };
};

// Enhanced scan results response with both scan info and devices
export interface ScanResultsResponse {
  scan: ScanObject;
  network_devices: NetworkDevice[];
  snmp_query_status?: SNMPQueryStatus;
  ssh_query_status?: SSHQueryStatus;
}

// Scan results API function
export const fetchScanResults = async (scanId: string): Promise<ScanResultsResponse> => {
  if (!API_TOKEN) {
    throw new Error('API token not configured');
  }

  if (!scanId || scanId.trim().length === 0) {
    throw new Error('Scan ID is required');
  }

  try {
    const response = await apiClient.get<ScanResultsApiResponse>(`/scans/${encodeURIComponent(scanId)}`);
    
    // Handle null NetworkDevices as "0 devices discovered" (valid business case)
    const networkDevices = response.data.NetworkDevices 
      ? response.data.NetworkDevices.map(mapRawDeviceToNetworkDevice)
      : []; // Empty array = 0 devices found
    
    // Log scan results for debugging
    if (!response.data.NetworkDevices) {
      console.log(`Scan ${scanId} completed with 0 devices discovered`);
    } else {
      console.log(`Scan ${scanId} completed with ${response.data.NetworkDevices.length} devices discovered`);
    }
    
    return {
      scan: response.data.Scan,
      network_devices: networkDevices,
      snmp_query_status: response.data.SNMPQueryStatus,
      ssh_query_status: response.data.SSHQueryStatus
    };
  } catch (error) {
    console.error('fetchScanResults failed for scanId:', scanId);
    console.error('Full error object:', error);
    
    if (axios.isAxiosError(error)) {
      console.error('Axios error details:');
      console.error('- Status:', error.response?.status);
      console.error('- Status Text:', error.response?.statusText);
      console.error('- Response Data:', error.response?.data);
      console.error('- Request URL:', error.config?.url);
      console.error('- Request Method:', error.config?.method);
      console.error('- Request Headers:', error.config?.headers);
      
      if (error.response?.status === 404) {
        throw new Error('Scan not found or no results available');
      }
      throw new Error(`Failed to fetch scan results: ${error.response?.status} ${error.response?.statusText}`);
    }
    
    console.error('Non-axios error:', error);
    throw new Error('Failed to fetch scan results');
  }
};

// Excel export API function
export const downloadScanExcel = async (scanId: string): Promise<void> => {
  if (!API_TOKEN) {
    throw new Error('API token not configured');
  }

  if (!scanId || scanId.trim().length === 0) {
    throw new Error('Scan ID is required');
  }

  try {
    const response = await apiClient.get(`/scans/${encodeURIComponent(scanId)}/eip`, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    });

    // Create blob URL and trigger download
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `scan-${scanId}-export.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        throw new Error('Scan not found or no data available for export');
      }
      throw new Error(`Failed to download Excel file: ${error.response?.status} ${error.response?.statusText}`);
    }
    throw new Error('Failed to download Excel file');
  }
};

// Visio export API function
export const downloadScanVisio = async (scanId: string): Promise<void> => {
  if (!API_TOKEN) {
    throw new Error('API token not configured');
  }

  if (!scanId || scanId.trim().length === 0) {
    throw new Error('Scan ID is required');
  }

  try {
    const response = await apiClient.get(`/scans/${encodeURIComponent(scanId)}/visio`, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/vnd.visio'
      }
    });

    // Create blob URL and trigger download
    const blob = new Blob([response.data], {
      type: 'application/vnd.visio'
    });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    link.download = `scan-${scanId}-diagram-${timestamp}.vsdx`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        throw new Error('Scan not found or no Visio diagram available');
      }
      throw new Error(`Failed to download Visio file: ${error.response?.status} ${error.response?.statusText}`);
    }
    throw new Error('Failed to download Visio file');
  }
};

// Helper function to sanitize site name for filename
const sanitizeSiteName = (siteName: string): string => {
  if (!siteName || siteName.trim().length === 0) {
    return 'UnknownSite';
  }
  // Replace spaces with underscores and remove special characters
  return siteName.trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
};

// Helper function to generate timestamp for filename
const generateTimestamp = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
};

// Wrapper function for Excel export with custom filename
export const downloadScanExcelWithCustomName = async (scanId: string, siteName: string): Promise<void> => {
  if (!API_TOKEN) {
    throw new Error('API token not configured');
  }

  if (!scanId || scanId.trim().length === 0) {
    throw new Error('Scan ID is required');
  }

  try {
    const response = await apiClient.get(`/scans/${encodeURIComponent(scanId)}/eip`, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    });

    // Create blob URL and trigger download
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Generate custom filename: Scan_SiteName_DateAndTime.xlsx
    const sanitizedSiteName = sanitizeSiteName(siteName);
    const timestamp = generateTimestamp();
    link.download = `Scan_${sanitizedSiteName}_${timestamp}.xlsx`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        throw new Error('Scan not found or no data available for export');
      }
      throw new Error(`Failed to download Excel file: ${error.response?.status} ${error.response?.statusText}`);
    }
    throw new Error('Failed to download Excel file');
  }
};

// Wrapper function for EIP export with custom filename (same as Excel but different internal endpoint)
export const downloadScanEIPWithCustomName = async (scanId: string, siteName: string): Promise<void> => {
  // For now, this is the same as Excel export since they use the same endpoint
  // If EIP has a different endpoint in the future, it can be updated here
  return downloadScanExcelWithCustomName(scanId, siteName);
};
